<template>
  <div class="w-full flex flex-col">
    <!-- Header/Opener -->
    <div class="w-full flex items-center p-2">
      <!-- User Info -->
      <div class="relative flex items-center gap-2 min-w-0">
        <div class="flex items-center justify-center flex-shrink-0 w-9 h-9">
          <img v-if="ownerUser.avatar" :src="ownerUser.avatar" alt="User Avatar" class="w-6 h-6 rounded-full flex-shrink-0"
          @mouseenter="showOwnerTooltip = true" @mouseleave="showOwnerTooltip = false">
          <DefaultAvatarIcon v-else class="w-6 h-6 rounded-full flex-shrink-0"
          @mouseenter.native="showOwnerTooltip = true" @mouseleave.native="showOwnerTooltip = false" />
        </div>
        <div class="flex-grow flex flex-col min-w-0">
          <BaseText type="label" size="sm" class="text-text-muted w-full truncate">
            {{ account.name }}
          </BaseText>
          <BaseText type="body" size="xs" class="text-text-normal w-full truncate">
            {{ account.email }}
          </BaseText>
        </div>
        <!-- Account Owner Info Tooltip -->
        <transition>
          <div v-if="showOwnerTooltip" class="owner-tooltip flex flex-col gap-0.5 px-2 py-1 shadow-sm pointer-events-none">
            <div class="flex items-center gap-1.5">
              <BaseText type="body" size="xs">
                Owner:
              </BaseText>
              <BaseText type="label" size="xs">
                {{ ownerUser.name || `${ownerUser.first_name} ${ownerUser.last_name}` }}
              </BaseText>
            </div>
            <BaseText type="body" size="xs" class="text-neutral-alpha-650">
              {{ ownerUser.email }}
            </BaseText>
          </div>
        </transition>
      </div>
      <div class="flex items-center gap-x-2 gap-y-0.5 flex-wrap ml-5">
        <!-- Guest Access Indicator -->
        <div v-if="isAccountGuest" class="flex items-center gap-1 pl-0.5 py-0.5 pr-1.5 rounded-md bg-neutral-25">
          <GuestAccessIcon class="text-icon-normal" :width="16" :height="16" />
          <BaseText type="label" size="xs" class="text-text-normal">
            Guest Access
          </BaseText>
        </div>
        <!-- Connection Status -->
        <div class="flex items-center gap-1 pl-0.5 py-0.5 pr-1.5 rounded-md"
        :class="account.connected ? 'bg-secondary-green-10 text-secondary-green-300' : 'bg-secondary-yellow-10 text-secondary-yellow-300'">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="none">
            <path d="M10.0001 11.333V12.6663M10.0001 11.333C10.0001 10.5966 9.40317 9.99967 8.66679 9.99967H7.66679C6.93041 9.99967 6.33346 9.40272 6.33346 8.66634V7.33301M10.0001 11.333C10.0001 10.5966 10.5971 9.99967 11.3335 9.99967H12.3335C13.0698 9.99967 13.6668 9.40272 13.6668 8.66634V7.33301M8.16679 5.66634C8.16679 6.67886 7.34598 7.49967 6.33346 7.49967C5.32093 7.49967 4.50012 6.67886 4.50012 5.66634C4.50012 4.65382 5.32093 3.83301 6.33346 3.83301C7.34598 3.83301 8.16679 4.65382 8.16679 5.66634ZM15.5001 5.66634C15.5001 6.67886 14.6793 7.49967 13.6668 7.49967C12.6543 7.49967 11.8335 6.67886 11.8335 5.66634C11.8335 4.65382 12.6543 3.83301 13.6668 3.83301C14.6793 3.83301 15.5001 4.65382 15.5001 5.66634ZM11.8335 14.333C11.8335 15.3455 11.0126 16.1663 10.0001 16.1663C8.9876 16.1663 8.16679 15.3455 8.16679 14.333C8.16679 13.3205 8.9876 12.4997 10.0001 12.4997C11.0126 12.4997 11.8335 13.3205 11.8335 14.333Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
          </svg>
          <BaseText type="label" size="xs">
            {{ account.connected ? 'Connected' : 'Disconnected' }}
          </BaseText>
        </div>
        <!-- Total Accounts -->
        <div v-if="noAccounts" class="flex items-center gap-1 pl-0.5 py-0.5 pr-1.5 rounded-md bg-secondary-yellow-10 text-secondary-yellow-300">
          <WarningIcon :width="16" :height="16" />
          <BaseText type="label" size="xs">
            This account has no associated ad accounts
          </BaseText>
        </div>
        <BaseText v-else type="label" size="xs" class="text-text-muted px-1.5 py-0.5 rounded-md bg-neutral-25">
          {{ account.adAccounts.length }} {{ account.adAccounts.length === 1 ? 'Account' : 'Accounts' }}
        </BaseText>
      </div>
      <div class="ml-auto flex items-center gap-2 flex-shrink-0">
        <!-- Edit Connection Button -->
        <div class="relative">
          <button class="flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
          :class="editConnectionDisabled ? 'bg-neutral-25 cursor-default' : 'hover:bg-neutral-25'"
          @mouseenter="showEditDisabledTooltip = true" @mouseleave="showEditDisabledTooltip = false"
          @click="handlePlatformAuthenticate(platform)" :disabled="loadingAuthURL">
            <BaseLoadingSpinnerCircle v-if="loadingAuthURL" class="text-icon-disabled" :duration="1.2" />
            <EditSquareIcon v-else :class="editConnectionDisabled ? 'text-icon-disabled' : 'text-icon-normal'" />
            <BaseText type="label" size="sm" :class="editConnectionDisabled ? 'text-text-disabled' : 'text-text-muted'">
              Manage
            </BaseText>
          </button>
          <!-- Only Owner can edit connection tooltip -->
          <transition>
            <div v-if="editConnectionDisabled && !isOwner && showEditDisabledTooltip" class="edit-disabled-tooltip px-2 py-1 shadow-sm">
              <BaseText type="body" size="sm">
                Only the account owner can edit the connection.
              </BaseText>
            </div>
          </transition>
        </div>
        <!-- Opener Chevron Button -->
        <button v-if="!noAccounts" class="w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-100 hover:bg-neutral-25"
        @click="isExpanded = !isExpanded">
          <div class="transform transition-transform" :style="{ transform: isExpanded ? 'scaleY(-1)' : '' }">
            <ChevronIcon class="text-icon-normal" />
          </div>
        </button>
      </div>
    </div>
    <!-- Content -->
    <div ref="contentContainer" class="relative w-full overflow-hidden">
      <transition name="content">
        <div v-if="isExpanded && !noAccounts" class="w-full flex flex-col gap-2 px-2 pb-2 pt-0.5">
          <!-- Ad Accounts List -->
          <div v-for="(adAccount, index) in adAccounts" :key="`${account.id}-${adAccount.id}`" class="ad-account-row py-0.5">
            <!-- Ad Account Index -->
            <BaseText type="label" size="xs" class="p-0.5 text-center rounded-md text-text-muted bg-neutral-25">
              {{ index + 1 }}
            </BaseText>
            <!-- Name & Info -->
            <div class="flex items-center gap-3">
              <BaseText type="label" size="sm" class="text-text-muted truncate">
                {{ adAccount.name }}
              </BaseText>
              <BaseText type="body" size="sm" class="text-text-normal">
                {{ formatAccountId(adAccount.id) }}
              </BaseText>
            </div>
            <!-- Associated Lens Brands -->
            <div class="flex items-center gap-x-1.5 gap-y-0.5 flex-wrap">
              <div v-for="lens in adAccount.lenses" :key="`${adAccount.id}-${lens.id}`"
              class="flex items-center gap-1.5 pl-0.5 py-0.5 pr-1.5 rounded-md bg-neutral-25">
                <img v-if="lens.avatar" :src="lens.avatar" :alt="lens.name" class="w-5 h-5" style="border-radius: 4px;">
                <div v-else class="h-5 flex items-center justify-center bg-neutral-400 p-0.5" style="min-width: 20px; border-radius: 4px;">
                  <BaseText type="label" size="xs" class="text-white">
                    {{ getInitials(lens.name) }}
                  </BaseText>
                </div>
                <BaseText type="label" size="xs" class="text-text-muted">
                  {{ lens.name }}
                </BaseText>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
    <!-- Authentication Controller Interface -->
    <LensPlatformAuthenticator 
      ref="platformAuthenticator"
      :loading="loadingAuthURL"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import smoothReflow from 'vue-smooth-reflow'

// Controllers
import LensPlatformAuthenticator from '../../lens/LensPlatformAuthenticator.vue'

// Icons
import DefaultAvatarIcon from '../../globals/Icons/DefaultAvatarIcon.vue'
import ChevronIcon from '../../globals/Icons/ChevronIcon.vue'
import ConnectionIcon from '../../globals/Icons/ConnectionIcon.vue'
import EditSquareIcon from '../../globals/Icons/EditSquareIcon.vue'
import WarningIcon from '../../globals/Icons/WarningIcon.vue'
import GuestAccessIcon from '../../globals/Icons/LensIcons/GuestAccessIcon.vue'

export default {
  name: 'DataSourceCard',
  mixins: [smoothReflow],
  components: {
    LensPlatformAuthenticator,
    DefaultAvatarIcon,
    ChevronIcon,
    ConnectionIcon,
    EditSquareIcon,
    WarningIcon,
    GuestAccessIcon
  },
  props: {
    platform: {
      type: String,
      required: true
    },
    account: {
      type: Object,
      required: true
    },
    initExpanded: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isExpanded: this.initExpanded,
      loadingAuthURL: this.loadingAuthURL,
      showEditDisabledTooltip: false,
      showOwnerTooltip: false
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.$smoothReflow({
        el: this.$refs.contentContainer,
        property: 'height',
        transition: 'height 300ms ease-in-out'
      })
    })
  },
  beforeDestroy () {
    this.$unsmoothReflow({ el: this.$refs.contentContainer })
  },
  computed: {
    ...mapGetters('LensModule', ['getSidebarLenses']),
    ...mapGetters('AuthModule', ['getUser']),
    ownerUser () {
      return this.account.ownerUser || { name: 'External user', avatar: null, email: null }
    },
    isOwner () {
      return this.account.ownerUser?.id === this.getUser.user_id
    },
    adAccounts () {
      return this.account?.adAccounts?.map(adAccount => ({
        ...adAccount,
        lenses: [...this.getSidebarLenses].filter(lens => lens.accounts.some(account => account.account_id === adAccount.id))
      })) ?? []
    },
    noAccounts () {
      return !this.account?.adAccounts?.length
    },
    editConnectionDisabled () {
      return this.loadingAuthURL || !this.isOwner
    },
    isAccountGuest () {
      return this.account?.accessType === 'guest'
    }
  },
  methods: {
    async handlePlatformAuthenticate (platform) {
      this.loadingAuthURL = true
      try {
        await this.$refs.platformAuthenticator.handlePlatformAuthenticate(platform)
        this.$emit('refresh-ad-accounts')
      } catch (error) {
        console.error('Failed to authenticate platform:', error)
      } finally {
        this.loadingAuthURL = false
      }
    },
    formatAccountId (id) {
      return this.platform === 'meta' ? id.slice(4) : id
    },
    getInitials (name) {
      if (!name) return ''
      return name.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2)
    }
  }
}
</script>

<style scoped>
.ad-account-row {
  display: grid;
  grid-template-columns: 20px 380px 1fr;
  column-gap: 8px;
  align-items: start;
}
.edit-disabled-tooltip {
  position: absolute;
  z-index: 50;
  left: 50%;
  bottom: calc(100% + 8px);
  transform: translateX(-50%);
  border-radius: 6px;
  background-color: #131520;
  color: white;
  width: 180px;
}
.owner-tooltip {
  position: absolute;
  z-index: 50;
  left: 0px;
  bottom: calc(100% + 8px);
  border-radius: 6px;
  background-color: #131520;
  color: white;
}

.content-enter-active, .content-leave-active {
  transition: opacity 300ms ease-in-out;
}
.content-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}
.content-enter-from, .content-enter, .content-leave-to {
  opacity: 0;
}
.content-enter-to, .content-leave-from {
  opacity: 1;
}

.v-enter-active, .v-leave-active {
  transition: opacity 200ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>